/**
 * AdvancedImageBlock Component
 *
 * Custom BlockNote React component for displaying images with enhanced features:
 * - Link assignment (make images clickable)
 * - Media browser integration (select from existing library)
 * - Alt text support with filename fallback (SEO optimization)
 *
 * This is a custom block type (advancedImage) separate from the default BlockNote
 * image block to allow full schema control and avoid extension issues.
 *
 * Features:
 * - Image upload via file selection or drag-and-drop
 * - Browse existing images from media library
 * - Link URL with target and nofollow options
 * - Alt text field with character counter and warning
 * - Caption support
 * - Edit/View mode distinction
 *
 * Props (from block.props):
 * - url: Image URL
 * - caption: Image caption
 * - alt: Alt text for accessibility and SEO
 * - linkUrl: Optional link URL to wrap image
 * - linkTarget: Link target (_blank, _self)
 * - linkNofollow: Whether to add rel="nofollow"
 * - blobSignedId: ActiveStorage blob signed ID (for SSR variant generation)
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { sanitizeUrl, normalizeWebUrl } from "../../utils/sanitize";
import ImageUploadField from "./shared/ImageUploadField.jsx";

// Constants
const ALT_TEXT_MAX_LENGTH = 200;

/**
 * Extracts a readable filename from a URL for alt text fallback
 */
function extractFilenameFromUrl(url) {
  if (!url) return "";
  try {
    const urlObj = new URL(url, window.location.origin);
    const pathname = urlObj.pathname;
    const filename = pathname.split("/").pop();
    if (!filename) return "";
    // Remove extension and decode
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
    const decoded = decodeURIComponent(nameWithoutExt);
    // Replace dashes/underscores with spaces, capitalize words
    return decoded
      .replace(/[-_]/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .trim();
  } catch {
    return "";
  }
}

/**
 * Determines if a URL is internal (same domain or starts with /)
 */
function isInternalUrl(url) {
  if (!url) return false;
  if (url.startsWith("/")) return true;
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.hostname === window.location.hostname;
  } catch {
    return false;
  }
}

/**
 * Edit Mode Component
 *
 * Provides form fields for editing all advanced image properties
 */
function AdvancedImageEditor({ block, onUpdate }) {
  const props = block.props || {};

  const [touched, setTouched] = useState({
    url: false,
    alt: false,
  });

  // Collapse details by default if image already has content
  const [collapsed, setCollapsed] = useState(() => {
    const p = block.props || {};
    const hasContent = Boolean(p.url && p.url.trim());
    return hasContent;
  });

  // Track whether link settings section is expanded
  const [linkSectionExpanded, setLinkSectionExpanded] = useState(
    Boolean(props.linkUrl),
  );

  // Handle image upload callback
  const handleImageUploaded = (url, variants, blobData) => {
    if (blobData?.signed_id) {
      onUpdate("blobSignedId", blobData.signed_id);
    }
    // Use the 1000px variant for advanced images, or fall back to original
    const imageUrl = variants?.["1000px"] || url;
    onUpdate("url", imageUrl);
  };

  // Handle link URL change with auto-detection of internal vs external
  const handleLinkUrlChange = (url) => {
    onUpdate("linkUrl", url);
    // Auto-detect internal vs external and set target accordingly
    if (url && url.trim()) {
      const internal = isInternalUrl(url);
      onUpdate("linkTarget", internal ? "_self" : "_blank");
    }
  };

  // Handle link URL blur - normalize the URL
  const handleLinkUrlBlur = () => {
    const currentUrl = props.linkUrl || "";
    if (currentUrl.trim()) {
      const normalized = normalizeWebUrl(currentUrl);
      onUpdate("linkUrl", normalized);
    }
  };

  // Get alt text with fallback to filename
  const getDisplayAltText = () => {
    return props.alt || "";
  };

  const altTextLength = (props.alt || "").length;
  const hasAltTextWarning = !props.alt && props.url;

  return (
    <div className="advanced-image-editor w-full p-4 border border-gray-200 rounded-lg bg-gray-50">
      <span className="sr-only">Advanced Image Settings</span>

      {/* Image Upload */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Image
        </label>
        <ImageUploadField
          currentImageUrl={props.url}
          onImageUploaded={handleImageUploaded}
          blockType="advancedImage"
          blockId={block.id}
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/avif"
          maxSize={10 * 1024 * 1024}
          hasError={touched.url && !props.url}
          onTouched={() => setTouched((t) => ({ ...t, url: true }))}
        />
      </div>

      {/* Only show remaining fields if image is uploaded */}
      {props.url && (
        <>
          {/* Show/Hide Details Toggle */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => setCollapsed((v) => !v)}
              className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center"
              aria-expanded={!collapsed}
              aria-controls={`advanced-image-details-${block.id}`}
            >
              <svg
                className={`w-4 h-4 mr-1 transform transition-transform ${collapsed ? "" : "rotate-90"}`}
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M6 6l6 4-6 4V6z"
                  clipRule="evenodd"
                />
              </svg>
              {collapsed ? "Show details" : "Hide details"}
            </button>
            <div className="flex-1" />
          </div>

          {/* Collapsible Details Section */}
          {!collapsed && (
            <div id={`advanced-image-details-${block.id}`}>
              {/* Alt Text */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Alt Text
                  {hasAltTextWarning && (
                    <span
                      className="ml-2 text-yellow-600 inline-flex items-center"
                      title="Add alt text for accessibility and SEO"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={getDisplayAltText()}
                  onChange={(e) => onUpdate("alt", e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, alt: true }))}
                  maxLength={ALT_TEXT_MAX_LENGTH}
                  className={`form-control${hasAltTextWarning ? " border-yellow-400" : ""}`}
                  placeholder="Describe this image for screen readers"
                />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-neutral-500">
                    {hasAltTextWarning ? (
                      <span className="text-yellow-600">
                        Add alt text for accessibility
                      </span>
                    ) : (
                      "Helps screen readers and SEO"
                    )}
                  </span>
                  <span
                    className={`text-xs ${altTextLength > ALT_TEXT_MAX_LENGTH * 0.9 ? "text-yellow-600" : "text-neutral-500"}`}
                  >
                    {altTextLength}/{ALT_TEXT_MAX_LENGTH}
                  </span>
                </div>
              </div>

              {/* Caption */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Caption (optional)
                </label>
                <input
                  type="text"
                  value={props.caption || ""}
                  onChange={(e) => onUpdate("caption", e.target.value)}
                  maxLength={500}
                  className="form-control"
                  placeholder="Add a caption below the image"
                />
              </div>

              {/* Link Settings Section */}
              <div className="mb-4">
                <button
                  type="button"
                  onClick={() => setLinkSectionExpanded((v) => !v)}
                  className="flex items-center text-sm font-medium text-neutral-700 hover:text-neutral-900"
                  aria-expanded={linkSectionExpanded}
                >
                  <svg
                    className={`w-4 h-4 mr-1 transform transition-transform ${linkSectionExpanded ? "rotate-90" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6 6l6 4-6 4V6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Link Settings
                  {props.linkUrl && (
                    <span className="ml-2 text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded">
                      Linked
                    </span>
                  )}
                </button>

                {linkSectionExpanded && (
                  <div className="mt-3 pl-5 border-l-2 border-gray-200 space-y-3">
                    {/* Link URL */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Link URL
                      </label>
                      <input
                        type="text"
                        value={props.linkUrl || ""}
                        onChange={(e) => handleLinkUrlChange(e.target.value)}
                        onBlur={handleLinkUrlBlur}
                        className="form-control"
                        placeholder="https://example.com or /internal-page"
                      />
                      <span className="text-xs text-neutral-500 mt-1 block">
                        Leave empty for no link
                      </span>
                    </div>

                    {/* Link Options - only show if URL is set */}
                    {props.linkUrl && (
                      <div className="space-y-2">
                        {/* Open in new tab */}
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={props.linkTarget === "_blank"}
                            onChange={(e) =>
                              onUpdate(
                                "linkTarget",
                                e.target.checked ? "_blank" : "_self",
                              )
                            }
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-neutral-700">
                            Open in new tab
                          </span>
                          {isInternalUrl(props.linkUrl) ? (
                            <span className="ml-2 text-xs text-neutral-500">
                              (internal link detected)
                            </span>
                          ) : (
                            <span className="ml-2 text-xs text-neutral-500">
                              (external link)
                            </span>
                          )}
                        </label>

                        {/* Nofollow */}
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={props.linkNofollow === true}
                            onChange={(e) =>
                              onUpdate("linkNofollow", e.target.checked)
                            }
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-neutral-700">
                            Add nofollow
                          </span>
                          <span className="ml-2 text-xs text-neutral-500">
                            (prevents SEO link credit)
                          </span>
                        </label>

                        {/* Remove link button */}
                        <button
                          type="button"
                          onClick={() => {
                            onUpdate("linkUrl", "");
                            onUpdate("linkTarget", "_blank");
                            onUpdate("linkNofollow", false);
                          }}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Remove link
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * View Mode Component
 *
 * Renders the image in its final display format
 * matching the server-side rendered version
 */
function AdvancedImageViewer({ block }) {
  const props = block.props || {};
  const {
    url,
    caption = "",
    alt = "",
    linkUrl = "",
    linkTarget = "_blank",
    linkNofollow = false,
  } = props;

  // Sanitize URL
  const sanitizedUrl = useMemo(() => sanitizeUrl(url || ""), [url]);
  const sanitizedLinkUrl = useMemo(
    () => (linkUrl ? sanitizeUrl(linkUrl) : ""),
    [linkUrl],
  );

  // Get alt text with filename fallback
  const altText = useMemo(() => {
    if (alt) return alt;
    if (caption) return caption;
    return extractFilenameFromUrl(url);
  }, [alt, caption, url]);

  // Build rel attribute
  const relAttribute = useMemo(() => {
    const parts = [];
    if (linkTarget === "_blank") {
      parts.push("noopener");
    }
    if (linkNofollow) {
      parts.push("nofollow");
    }
    return parts.length > 0 ? parts.join(" ") : undefined;
  }, [linkTarget, linkNofollow]);

  if (!sanitizedUrl || sanitizedUrl === "#") {
    return (
      <div className="advanced-image-placeholder my-4 p-8 bg-gray-100 rounded-lg text-center text-neutral-500">
        No image selected
      </div>
    );
  }

  const imageElement = (
    <img
      src={sanitizedUrl}
      alt={altText}
      className="max-w-full h-auto rounded"
    />
  );

  const imageWithLink = sanitizedLinkUrl ? (
    <a
      href={sanitizedLinkUrl}
      target={linkTarget}
      rel={relAttribute}
      className="block"
    >
      {imageElement}
    </a>
  ) : (
    imageElement
  );

  return (
    <figure className="advanced-image my-4">
      {imageWithLink}
      {caption && (
        <figcaption className="mt-2 text-sm text-neutral-600 text-center">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/**
 * Main AdvancedImageBlock Component
 *
 * Handles mode detection and delegates rendering to appropriate sub-component
 */
export default function AdvancedImageBlock({ block, editor }) {
  const [isEditMode, setIsEditMode] = useState(true);

  // Detect edit/view mode
  useEffect(() => {
    setIsEditMode(editor?.isEditable ?? true);
  }, [editor]);

  // Update block property
  const updateProp = useCallback(
    (key, value) => {
      if (!editor) return;

      // Use editor.getBlock to get the current block state
      const currentBlock = editor.getBlock(block.id);
      if (!currentBlock) {
        console.error("[AdvancedImageBlock] Block not found:", block.id);
        return;
      }

      editor.updateBlock(currentBlock, {
        props: { ...currentBlock.props, [key]: value },
      });
    },
    [editor, block.id],
  );

  // Initialize default props if needed
  useEffect(() => {
    if (!block.props) {
      editor?.updateBlock(block, {
        props: {
          url: "",
          caption: "",
          alt: "",
          linkUrl: "",
          linkTarget: "_blank",
          linkNofollow: false,
          blobSignedId: "",
        },
      });
    }
  }, [block, editor]);

  if (isEditMode) {
    return <AdvancedImageEditor block={block} onUpdate={updateProp} />;
  }

  return <AdvancedImageViewer block={block} />;
}
